import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useSelector } from "react-redux";
import { Users, Headphones, Wallet, Briefcase, Building2 } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import { FilterDismissLink } from "../../components/dashboard/BackNavLink";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatusBadge from "../../components/dashboard/StatusBadge";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import { CUSTOMERS_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import { USE_MOCK_AUTH } from "../../config/api";
import { MOCK_CUSTOMERS, MOCK_CASES, formatCurrency } from "../../data/mock/dashboardData";
import {
  fetchOpsCustomers,
  fetchOpsCustomersSummary,
  normalizeOpsCustomer,
} from "../../services/institutionWorkspaceApi";
import { showError } from "../../utils/toast";
import { personNameColumns } from "../../utils/personName";
import useRole from "../../hooks/useRole";
import { opsCasesPath } from "../../utils/opsCasesPath";

function enrichMockCustomer(customer) {
  const cases = MOCK_CASES.filter((c) => c.customer === customer.name);
  const primary = cases[0];
  const amountOwed = customer.amountOwed;
  const amountPaid = customer.amountPaid;
  return {
    ...customer,
    numericId: Number(String(customer.id).replace(/\D/g, "")) || null,
    caseId: primary?.id ?? null,
    accountNumber: customer.accountNumber || customer.account_number || "",
    phone: customer.phone || "",
    customer_number: customer.customer_number || "",
    email: customer.email || "",
    ghana_card_number: customer.ghana_card_number || "",
    gender: customer.gender || "",
    region: customer.region || primary?.region || "",
    country: customer.country || "",
    exact_area: customer.exact_area || "",
    cases_count: cases.length || 1,
    institution: primary?.institution ?? "—",
    institutionRecord: primary?.institution
      ? { name: primary.institution, logo: null, type: "", region: "", code: "", status: "" }
      : null,
    amountOwed,
    amountPaid,
    outstandingAmount: Math.max(0, amountOwed - amountPaid),
    daysOverdue: customer.daysOverdue,
    assignedAgent: primary?.assignedAgent ?? null,
    channel: primary?.channel ?? customer.lastContact,
    caseStatus: primary?.status ?? customer.status,
    status: primary?.status ?? customer.status,
    updatedAt: primary?.updatedAt ?? primary?.createdAt ?? customer.updatedAt ?? null,
  };
}

export default function CustomersPage() {
  const { isSuperAdmin, isExecutiveManager, isCallCenterManager, isLegalOfficer } = useRole();
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [searchParams] = useSearchParams();
  const customerParam = searchParams.get("customer");
  const institutionParam = searchParams.get("institution");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        if (!useLiveApi) {
          const mockRows = MOCK_CUSTOMERS.map(enrichMockCustomer);
          if (!cancelled) {
            setRows(mockRows);
            setSummary(null);
          }
          return;
        }

        const [customers, customerSummary] = await Promise.all([
          fetchOpsCustomers(token),
          fetchOpsCustomersSummary(token).catch(() => null),
        ]);
        if (cancelled) return;
        setRows((customers || []).map((row) => normalizeOpsCustomer(row)).filter(Boolean));
        setSummary(customerSummary);
      } catch (error) {
        if (!cancelled) {
          showError(error.message || "Could not load customers.");
          setRows([]);
          setSummary(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi]);

  const displayRows = useMemo(() => {
    let next = rows;
    if (institutionParam) {
      const needle = institutionParam.trim().toLowerCase();
      next = next.filter((row) => String(row.institution || "").toLowerCase() === needle);
    }
    if (customerParam) {
      const needle = customerParam.trim().toLowerCase();
      next = next.filter((row) => String(row.name || "").toLowerCase() === needle);
    }
    return next;
  }, [rows, institutionParam, customerParam]);

  const initialFilters = useMemo(() => {
    if (customerParam) return { name: [customerParam] };
    if (institutionParam) return { institution: [institutionParam] };
    return undefined;
  }, [customerParam, institutionParam]);

  const columns = useMemo(
    () => [
      { key: "numericId", header: "ID", type: "id" },
      { key: "updatedAt", header: "Date & time", type: "datetime" },
      { key: "accountNumber", header: "Account", type: "id" },
      ...personNameColumns(),
      { key: "institution", header: "Institution", type: "institution" },
      { key: "phone", header: "Mobile", type: "phone" },
      { key: "customer_number", header: "Customer no.", type: "id" },
      { key: "email", header: "Email", type: "text" },
      { key: "ghana_card_number", header: "Ghana Card", type: "id" },
      { key: "gender", header: "Gender", type: "text" },
      { key: "region", header: "Region", type: "text" },
      { key: "country", header: "Country", type: "text" },
      { key: "exact_area", header: "Exact area", type: "text" },
      { key: "caseId", header: "Case ID", type: "id" },
      { key: "cases_count", header: "Cases", type: "number" },
      { key: "amountOwed", header: "Amount owed", type: "currency" },
      { key: "amountPaid", header: "Amount paid", type: "currency" },
      { key: "outstandingAmount", header: "Outstanding", type: "currency" },
      {
        key: "daysOverdue",
        header: "Days overdue",
        type: "number",
        render: (row) => (
          <span
            className={`tabular-nums text-sm font-bold ${
              row.daysOverdue > 60
                ? "text-red-600"
                : row.daysOverdue > 30
                  ? "text-amber-600"
                  : "text-emerald-600"
            }`}
          >
            {row.daysOverdue}d
          </span>
        ),
      },
      {
        key: "status",
        header: "Status",
        type: "badge",
        render: (row) => <StatusBadge status={row.status || row.caseStatus} />,
      },
      {
        key: "actions",
        header: "Actions",
        align: "center",
        headerClassName: "text-center",
        actions: (row) => [
          {
            key: "cases",
            label: "View collection cases",
            description: `All recovery cases for ${row.name}`,
            icon: Briefcase,
            tone: "primary",
            to: opsCasesPath({
              account: row.accountNumber || row.account_number,
              caseId: row.caseId || row.case_id,
            }),
          },
          {
            key: "payments",
            label: "View payments",
            description: "Payment activity for this customer",
            icon: Wallet,
            tone: "emerald",
            to: `/dashboard/payments?customer=${encodeURIComponent(row.name)}`,
          },
          {
            key: "contact",
            label: "Contact history",
            description: "Calls and outreach activity",
            icon: Headphones,
            tone: "sky",
            to: `/dashboard/contact-centre?customer=${encodeURIComponent(row.name)}&tab=calls`,
          },
        ],
      },
    ],
    []
  );

  return (
    <PageShell
      title="Customers"
      description={
        isSuperAdmin || isExecutiveManager
          ? "Unique debtors across institutions — KYC, amounts, and institution branding."
          : isCallCenterManager
            ? "Customers on cases for the institutions assigned to you."
            : isLegalOfficer
              ? "Customers on escalations assigned to you."
              : "Customers on cases assigned to you."
      }
    >
      {loading ? (
        <div className="mb-3 rounded-xl border border-primary/[0.07] bg-primary/[0.02] px-4 py-3 text-xs text-primary/50">
          Loading customers…
        </div>
      ) : null}

      {(customerParam || institutionParam) && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/[0.04] to-accent/[0.06] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
              {institutionParam ? <Building2 className="h-4 w-4" /> : <Users className="h-4 w-4" />}
            </span>
            <div>
              <p className="text-xs font-semibold text-primary">
                {institutionParam ? "Customers for institution" : "Filtered customer"}
              </p>
              <p className="text-sm font-bold text-primary">
                {institutionParam ?? customerParam}
              </p>
            </div>
          </div>
          <FilterDismissLink fallback="/dashboard/customers" fallbackLabel="Show all customers" />
        </div>
      )}

      <StatCardGrid cols={4}>
        <StatCard
          label="Customers"
          value={summary?.customers ?? displayRows.length}
          icon={Users}
          tone="primary"
        />
        <StatCard
          label="Total owed"
          value={formatCurrency(
            summary?.total_owed ??
              displayRows.reduce((sum, row) => sum + (row.amountOwed || 0), 0)
          )}
          icon={Wallet}
          tone="accent"
        />
        <StatCard
          label="Paid"
          value={formatCurrency(
            summary?.total_paid ??
              displayRows.reduce((sum, row) => sum + (row.amountPaid || 0), 0)
          )}
          icon={Wallet}
          tone="emerald"
        />
        <StatCard
          label="Outstanding"
          value={formatCurrency(
            displayRows.reduce((sum, row) => sum + (row.outstandingAmount || 0), 0)
          )}
          icon={Wallet}
          tone="sky"
        />
      </StatCardGrid>

      <FilteredDataTable
        key={customerParam ?? institutionParam ?? "all"}
        data={displayRows}
        columns={columns}
        config={{
          ...CUSTOMERS_FILTER_CONFIG,
          title: "Customer filters",
          searchKeys: [
            "name",
            "phone",
            "accountNumber",
            "customer_number",
            "email",
            "ghana_card_number",
            "id",
            "caseId",
            "numericId",
            "institution",
            "debt_customer_slug",
            "region",
          ],
          searchPlaceholder: "Search name, account, phone, Ghana Card, institution…",
          fields: [
            { key: "institution", label: "Institution", type: "multi", optionsFrom: "institution", section: "Institution" },
            { key: "region", label: "Region", type: "multi", optionsFrom: "region", section: "KYC" },
            { key: "gender", label: "Gender", type: "multi", optionsFrom: "gender", section: "KYC" },
            { key: "status", label: "Status", type: "multi", optionsFrom: "status", section: "Status" },
            { key: "amountOwed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
            { key: "amountPaid", label: "Amount paid (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
            { key: "outstandingAmount", label: "Outstanding (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
            { key: "daysOverdue", label: "Days overdue", type: "range", min: 0, max: 365, section: "Amounts & ageing" },
          ],
          metrics: [],
        }}
        initialFilters={initialFilters}
        pageSize={10}
        emptyMessage={
          isSuperAdmin || isExecutiveManager
            ? "No customers found across institutions."
            : "No customers in your book."
        }
      />
    </PageShell>
  );
}
